import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Avatar from "../../../../parts/avatar";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip-portal";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import { formatNumber, formatStripeDate } from "../../../../utils/format-sub";
import { truncateText } from "../../../../utils/string-helpers";
import { getDivisor } from "../../../../utils/stripe-helpers";
import { ViewContractButton } from "../../../athlete-portal/profile/components/ViewContractButton";

const DetailsComponent = ({
  sub,
  toggleDrawer,
  type = "membership",
  handleCancel = () => {},
  subscriberInvoices = {},
  setStep,
  api,
}) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();
  const {
    invoices: initialInvoices,
    grossRevenue,
    netRevenue,
    referral,
    hasMore: initialHasMore,
    nextStartingAfter: initialCursor,
  } = subscriberInvoices;

  const [invoices, setInvoices] = useState(initialInvoices);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [cursor, setCursor] = useState(initialCursor);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (initialInvoices) {
      setInvoices(initialInvoices);
      setHasMore(initialHasMore);
      setCursor(initialCursor);
    }
  }, [initialInvoices, initialHasMore, initialCursor]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api("subscriber-invoices", {
        query: {
          customer: sub.customer,
          productType: type === "membership" ? "PRESS" : "PUSH",
          startingAfter: cursor,
          // Same account the controller loaded page 1 for — without it an
          // organization staff user pages through their own account.
          ...(locals.activeUser?.organizationId && {
            organizationUser: locals.activeUserToRequest?._id,
          }),
        },
      });
      const data = await res.json();
      setInvoices((prev) => [...(prev || []), ...(data.invoices || [])]);
      setHasMore(data.hasMore);
      setCursor(data.nextStartingAfter);
    } finally {
      setLoadingMore(false);
    }
  };

  const divisor = getDivisor(sub?.currency);
  const hasFacilikey = !!sub?.plan?.metadata?.facilikey;
  // A pass is a one-off PaymentIntent, not a subscription — there is no plan
  // to change and nothing for cancel-subscriber to cancel.
  const canManage = !sub?.canceled && !sub?.isPass;

  return (
    <>
      {sub?.retryStatus && (
        <div className="chip warning member-details-banner">
          {t("membershipList.retryBanner", {
            attempt: sub.retryStatus.attemptCount,
            date: sub.retryStatus.nextPaymentAttempt
              ? formatStripeDate(sub.retryStatus.nextPaymentAttempt)
              : t("membershipList.retryBannerNoDate"),
          })}
        </div>
      )}
      <header className="details-header">
        <hgroup>
          {sub?.customer_avatar ? (
            <Avatar
              size="96"
              url={sub.customer_avatar.url}
              user={{}}
              height="auto"
            />
          ) : (
            <div className="subscriber-icon"></div>
          )}
          <div>
            <h2>{truncateText(sub?.customer_name, 30)}</h2>
            <p className="p1">{truncateText(sub?.customer_email, 40)}</p>
            {!!sub?.customer_token && (
              <div className="chip info">
                {t("membershipList.onboardingCode")} {sub?.customer_token}
              </div>
            )}
          </div>
        </hgroup>
        {/* biome-ignore lint/a11y/useValidAnchor lint/a11y/useSemanticElements: Pico styles links as buttons */}
        <a
          href="#_"
          onClick={toggleDrawer}
          role="button"
          data-button-type="tertiary"
          data-icon-only="true"
        >
          <img
            src="/img/new-icons/red-cross.svg"
            alt={t("membershipList.dismiss")}
          />
        </a>
      </header>

      <section>
        {type === "membership" && (
          <p className="p2">
            {t("membershipList.phoneNumberLabel")}{" "}
            <span>{sub?.phoneNumber}</span>
          </p>
        )}
        <p className="p2">
          {t("membershipList.joiningDate")}: <span>{sub?.created}</span>
        </p>
        <p className="p2">
          {t("membershipList.grossRevenue")}{" "}
          <span>{formatNumber(grossRevenue / divisor, sub?.currency)}</span>
        </p>
        <p className="p2">
          {t("membershipList.refundedRevenue")}{" "}
          <span>
            {formatNumber((grossRevenue - netRevenue) / divisor, sub?.currency)}
          </span>
        </p>
        <p className="p2">
          <div>
            {t("membershipList.netRevenue")}{" "}
            <ToolTip body={[t("membershipList.netRevenueTooltip")]}>
              <div className="info">
                <img src="/img/svg/question-circle.svg" alt="info" />
              </div>
            </ToolTip>
          </div>
          <span>{formatNumber(netRevenue / divisor, sub?.currency)}</span>
        </p>

        <p className="p2">
          {t("membershipList.planLabel")}{" "}
          <span>
            <span>{truncateText(sub?.plan.interval, 30)}</span>{" "}
            {sub?.canceled ? (
              <span className="text-(--danger)!">
                {t("membershipList.cancelled")}
              </span>
            ) : null}
          </span>
        </p>
        {sub?.mrr && (
          <p className="p2">
            {t("membershipList.mrrContribution")}{" "}
            <span>
              {formatNumber(sub.mrr.monthly, sub?.currency)} (
              {formatNumber(sub.mrr.perCycleAmount, sub?.currency)} ×{" "}
              {sub.mrr.factorLabel})
            </span>
          </p>
        )}
        {hasFacilikey && type === "membership" && (
          <p className="p2">
            <div>
              Facilikey{" "}
              <button
                onClick={() => {
                  submit("/actions/user/facilikey-resync", {
                    method: "POST",
                    body: {
                      customer: sub.customer,
                    },
                  });
                }}
                className="p-0 text-(--information) text-md! underline"
                type="button"
              >
                {t("membershipList.facilikeyResync")}
              </button>
            </div>
            <span>{t("membershipList.facilikeyYes")}</span>
          </p>
        )}
        <p className="p2">
          {t("membershipList.lastPayment")}:{" "}
          <span>{sub?.lastPayment || sub?.current_period_start}</span>
        </p>
        {type !== "membership" && (
          <>
            <h4 className="mt-3 mb-2">{t("membershipList.howTheyJoined")}</h4>
            <p className="p2">
              {t("membershipList.source")}{" "}
              <span>
                {t(
                  `membershipList.sourceLabel.${sub?.source?.source || "direct"}`
                )}
              </span>
            </p>
            {sub?.source?.flags?.coupon && (
              <p className="p2">{t("membershipList.usedACoupon")}</p>
            )}
            {!!referral && (
              <p className="p2">
                {t("membershipList.referredBy")}{" "}
                <span>
                  {referral?.referrerId?.name || "-"} (
                  {referral?.referrerId?.email || "-"})
                </span>
              </p>
            )}
            {subscriberInvoices.workoutsDuringTrial != null && (
              <p className="p2">
                {t("membershipList.workoutsDuringTrial", {
                  count: subscriberInvoices.workoutsDuringTrial,
                })}
              </p>
            )}
          </>
        )}
        <div
          className={`member-details-actions${canManage && type === "membership" ? " member-details-actions-flex-col" : ""}${sub?.canceled ? " member-details-actions-canceled" : ""}`}
        >
          {canManage && type === "membership" ? (
            <>
              <div className="member-details-actions-row">
                {/* biome-ignore lint/a11y/useValidAnchor lint/a11y/useSemanticElements: Pico styles links as buttons */}
                <a
                  role="button"
                  data-button-type="outline"
                  href="#_"
                  className="secondary-button"
                  onClick={(e) => {
                    e.preventDefault();
                    setStep("change");
                  }}
                >
                  <span>{t("membershipList.changePlan")}</span>
                </a>
                {/* biome-ignore lint/a11y/useValidAnchor lint/a11y/useSemanticElements: Pico styles links as buttons */}
                <a
                  role="button"
                  data-button-type="secondary"
                  data-action="cancel"
                  href="#_"
                  className="cancel"
                  onClick={handleCancel}
                >
                  {t("membershipList.cancelMembership", {
                    type:
                      type === "membership"
                        ? t("membershipList.membershipType")
                        : t("membershipList.subscriptionType"),
                  })}
                </a>
              </div>
              {type === "membership" && (
                <ViewContractButton athlete={sub} api={api} />
              )}
            </>
          ) : (
            <>
              {type === "membership" && (
                <ViewContractButton athlete={sub} api={api} />
              )}
              {canManage && (
                // biome-ignore lint/a11y/useValidAnchor lint/a11y/useSemanticElements: Pico styles links as buttons
                <a
                  role="button"
                  data-button-type="secondary"
                  data-action="cancel"
                  href="#_"
                  className="cancel"
                  onClick={handleCancel}
                >
                  {t("membershipList.cancelMembership", {
                    type:
                      type === "membership"
                        ? t("membershipList.membershipType")
                        : t("membershipList.subscriptionType"),
                  })}
                </a>
              )}
            </>
          )}
        </div>
      </section>

      <section>
        {type === "membership" && (
          <p className="p2 justify-start gap-2">
            {t("membershipList.contractSigned")}{" "}
            <img
              src="/img/new-icons/check-gradient.svg"
              alt={t("membershipList.checkAlt")}
              height={14}
              width={14}
            />
          </p>
        )}
        <details>
          <summary>
            <SvgIcon src={"/img/new-icons/arrow-right.svg"} />
            {t("membershipList.parq")}
          </summary>
          {sub?.parqForm ? (
            <div>
              <div>
                <p className="p2">{t("membershipList.parqHeartCondition")}</p>
                <span>
                  {sub.parqForm.heartCondition
                    ? ` ${t("membershipList.yes")}`
                    : ` ${t("membershipList.no")}`}
                </span>
              </div>
              <div>
                <p className="p2">{t("membershipList.parqChestPain")}</p>
                <span>
                  {sub.parqForm.chestPain
                    ? ` ${t("membershipList.yes")}`
                    : ` ${t("membershipList.no")}`}
                </span>
              </div>
              <div>
                <p className="p2">
                  {t("membershipList.parqPastMonthChestPain")}
                </p>
                <span>
                  {sub.parqForm.pastMonthChestPain
                    ? ` ${t("membershipList.yes")}`
                    : ` ${t("membershipList.no")}`}
                </span>
              </div>
              <div>
                <p className="p2">{t("membershipList.parqDizziness")}</p>
                <span>
                  {sub.parqForm.dizzinesConsciousnessLoss
                    ? ` ${t("membershipList.yes")}`
                    : ` ${t("membershipList.no")}`}
                </span>
              </div>
              <div>
                <p className="p2">{t("membershipList.parqBoneJoint")}</p>
                <span>
                  {sub.parqForm.boneJointProblem
                    ? ` ${t("membershipList.yes")}`
                    : ` ${t("membershipList.no")}`}
                </span>
              </div>
              <div>
                <p className="p2">{t("membershipList.parqBloodPressure")}</p>
                <span>
                  {sub.parqForm.bloodPressurePrescription
                    ? ` ${t("membershipList.yes")}`
                    : ` ${t("membershipList.no")}`}
                </span>
              </div>
              <div>
                <p className="p2 ">{t("membershipList.parqOtherReason")}</p>
                <span>
                  {sub.parqForm.reasonForNoPhysicalAct
                    ? ` ${t("membershipList.yes")}`
                    : ` ${t("membershipList.no")}`}
                </span>
              </div>
            </div>
          ) : (
            <p className="p2 text-center">
              {t("membershipList.parqNotCompleted")}
            </p>
          )}
        </details>
      </section>

      <section>
        <h3>{t("membershipList.paymentsHistory")}</h3>

        {/* biome-ignore lint/a11y/useValidAriaRole: custom role used for styling */}
        <section role="table-wrapper" data-borders-inside>
          {invoices?.length ? (
            // biome-ignore lint/a11y/useValidAriaRole: custom role used for styling
            <table role="report-table">
              <thead>
                <tr>
                  <th>{t("membershipList.date")}</th>
                  <th>{t("membershipList.amount")}</th>
                  <th>{t("membershipList.status")}</th>
                  <th />
                </tr>
              </thead>
              <tbody className="table-body-side-subscriber-list">
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{formatStripeDate(invoice.created)}</td>
                    <td>
                      {formatNumber(
                        invoice.amount_received / divisor,
                        invoice.currency
                      )}
                    </td>
                    <td>
                      {t(`membershipList.billingStatus.${invoice.status}`, {
                        defaultValue: invoice.status,
                      })}
                    </td>
                    <td>
                      {invoice.invoice ? (
                        <a
                          href={invoice.invoice.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="clipped"
                        >
                          {t("membershipList.invoice")}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center">{t("membershipList.noPaymentsFound")}</p>
          )}
          {hasMore && (
            <div className="py-2">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                data-button-type="tertiary"
                className="mx-auto"
              >
                {loadingMore
                  ? t("chat.loading", {
                      defaultValue: "Loading…",
                    })
                  : t("notifications.loadMore", { defaultValue: "Load more" })}
              </button>
            </div>
          )}
        </section>
      </section>
    </>
  );
};

export default DetailsComponent;
